import React, { useState, useEffect } from 'react';
import { Clock, Bell, BellOff, Coffee, Utensils, Home, Save, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = getBackendUrl() + '/api';

export const WorkSchedule = ({ user }) => {
  const [schedule, setSchedule] = useState({
    break_time: '10:00',
    break_enabled: true,
    lunch_time: '12:00',
    lunch_enabled: true,
    quit_time: '17:00',
    quit_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    fetchSchedule();
    checkNotificationPermission();
    scheduleAlarms();
  }, []);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications not supported on this device');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      toast.success('Notifications enabled!');
      scheduleAlarms();
    } else {
      toast.error('Notification permission denied');
    }
  };

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-schedule`);
      if (response.data) {
        setSchedule(response.data);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      // Use defaults if no schedule exists
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/work-schedule`, schedule);
      toast.success('Work schedule saved!');
      scheduleAlarms();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const scheduleAlarms = () => {
    if (!notificationsEnabled) return;

    // Clear existing alarms
    if (window.scheduledAlarms) {
      window.scheduledAlarms.forEach(id => clearTimeout(id));
    }
    window.scheduledAlarms = [];

    const now = new Date();
    const alarms = [
      { time: schedule.break_time, enabled: schedule.break_enabled, title: '☕ Break Time!', message: 'Time to take a break', icon: 'coffee' },
      { time: schedule.lunch_time, enabled: schedule.lunch_enabled, title: '🍽️ Lunch Time!', message: 'Time for lunch break', icon: 'lunch' },
      { time: schedule.quit_time, enabled: schedule.quit_enabled, title: '🏠 Quitting Time!', message: 'Work day is done!', icon: 'home' },
    ];

    alarms.forEach(alarm => {
      if (!alarm.enabled) return;

      const [hours, minutes] = alarm.time.split(':');
      const alarmTime = new Date();
      alarmTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // If alarm time has passed today, schedule for tomorrow
      if (alarmTime <= now) {
        alarmTime.setDate(alarmTime.getDate() + 1);
      }

      const timeUntilAlarm = alarmTime - now;

      const timeoutId = setTimeout(() => {
        showNotification(alarm.title, alarm.message);
        // Reschedule for next day
        scheduleAlarms();
      }, timeUntilAlarm);

      window.scheduledAlarms.push(timeoutId);
      
      console.log(`Scheduled ${alarm.title} for ${alarmTime.toLocaleString('en-CA', { timeZone: 'America/Toronto' })}`);
    });
  };

  const showNotification = (title, message) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
        requireInteraction: true,
      });

      // Play sound (optional)
      const audio = new Audio('/notification.mp3');
      audio.play().catch(() => console.log('Could not play sound'));

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  const testNotification = (type) => {
    const messages = {
      break: { title: '☕ Break Time!', message: 'Time to take a break' },
      lunch: { title: '🍽️ Lunch Time!', message: 'Time for lunch break' },
      quit: { title: '🏠 Quitting Time!', message: 'Work day is done!' },
    };
    
    const msg = messages[type];
    showNotification(msg.title, msg.message);
    toast.success('Test notification sent!');
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="glass-card p-8 text-center">
          <Clock className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-4xl">
      <div className="glass-card p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-xl">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Work Schedule Alarms</h2>
            <p className="text-slate-400 text-sm">Set reminders for breaks and end of day</p>
          </div>
        </div>

        {/* Notification Permission */}
        {!notificationsEnabled && (
          <div className="mb-6 p-4 bg-yellow-900/40 border-2 border-yellow-600 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-200 font-semibold mb-2">Notifications Disabled</p>
                <p className="text-yellow-100 text-sm mb-3">
                  Enable notifications to receive alarms at your scheduled times.
                </p>
                <Button
                  onClick={requestNotificationPermission}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Notifications
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Settings */}
        <div className="space-y-6">
          {/* Break Time */}
          <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Coffee className="w-6 h-6 text-blue-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Break Time</h3>
                  <p className="text-sm text-slate-400">Morning or afternoon break</p>
                </div>
              </div>
              <Switch
                checked={schedule.break_enabled}
                onCheckedChange={(checked) => setSchedule({ ...schedule, break_enabled: checked })}
              />
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor="break-time" className="text-white">Time</Label>
                <Input
                  id="break-time"
                  type="time"
                  value={schedule.break_time}
                  onChange={(e) => setSchedule({ ...schedule, break_time: e.target.value })}
                  disabled={!schedule.break_enabled}
                  className="mt-1 text-lg"
                />
              </div>
              <Button
                onClick={() => testNotification('break')}
                variant="outline"
                disabled={!notificationsEnabled}
                className="mb-0.5"
              >
                Test
              </Button>
            </div>
          </div>

          {/* Lunch Time */}
          <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Utensils className="w-6 h-6 text-green-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Lunch Time</h3>
                  <p className="text-sm text-slate-400">Midday meal break</p>
                </div>
              </div>
              <Switch
                checked={schedule.lunch_enabled}
                onCheckedChange={(checked) => setSchedule({ ...schedule, lunch_enabled: checked })}
              />
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor="lunch-time" className="text-white">Time</Label>
                <Input
                  id="lunch-time"
                  type="time"
                  value={schedule.lunch_time}
                  onChange={(e) => setSchedule({ ...schedule, lunch_time: e.target.value })}
                  disabled={!schedule.lunch_enabled}
                  className="mt-1 text-lg"
                />
              </div>
              <Button
                onClick={() => testNotification('lunch')}
                variant="outline"
                disabled={!notificationsEnabled}
                className="mb-0.5"
              >
                Test
              </Button>
            </div>
          </div>

          {/* Quitting Time */}
          <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Home className="w-6 h-6 text-orange-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Quitting Time</h3>
                  <p className="text-sm text-slate-400">End of work day</p>
                </div>
              </div>
              <Switch
                checked={schedule.quit_enabled}
                onCheckedChange={(checked) => setSchedule({ ...schedule, quit_enabled: checked })}
              />
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor="quit-time" className="text-white">Time</Label>
                <Input
                  id="quit-time"
                  type="time"
                  value={schedule.quit_time}
                  onChange={(e) => setSchedule({ ...schedule, quit_time: e.target.value })}
                  disabled={!schedule.quit_enabled}
                  className="mt-1 text-lg"
                />
              </div>
              <Button
                onClick={() => testNotification('quit')}
                variant="outline"
                disabled={!notificationsEnabled}
                className="mb-0.5"
              >
                Test
              </Button>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
          <p className="text-sm text-blue-200">
            💡 <strong>How it works:</strong> Your alarms will trigger daily at the times you set. 
            Make sure notifications are enabled and keep the app open in the background for best results.
          </p>
        </div>

        {/* Save Button */}
        <div className="mt-6">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 h-12 text-lg"
          >
            {saving ? (
              <>
                <Clock className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Schedule
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkSchedule;
